import { useTheme } from '@mui/material/styles'

import { useInsightsV2Store } from '../../../../common'
import type { DataSeries, DrilldownPayload } from '../../../../core'
import { DoubleBarChart } from '../../../components/chartblocks/DoubleBarChart'

import type { BlockProps } from '../../types'

import { buildComparisonSeries } from './comparisonUtils'
import { getLayoutConfig } from './layout'
import { actionBlocksKey } from '../../../mappings/actionBlocksKey'

/**
 * Card block for the double-bar chart. Mirrors BarchartBlock: it builds the shaped series
 * (buildComparisonSeries) and resolves the card palette (getLayoutConfig), then renders the reusable
 * DoubleBarChart and injects the card click behavior \u2014 open the shared drawer scoped to this beat
 * (chartBarIndex = clicked column, tabIndex = clicked sub-series).
 */
export const DoubleBarChartBlock = ({
  payload: { instance_slot, instance_slot_data, widget_type },
  beatGuid = '',
}: BlockProps) => {
  const theme = useTheme()
  const { openActionDrawer } = useInsightsV2Store()
  const { variant } = instance_slot
  const { ariaLabel = '', data_series, legends } = instance_slot_data
  const bars = buildComparisonSeries(data_series as DataSeries, ariaLabel)
  // getLayoutConfig returns undefined for an unknown/unsupported variant; fall back to an empty
  // palette so an unregistered beat can't throw on mount.
  const colors = getLayoutConfig(theme, variant)?.color ?? []

  return (
    <DoubleBarChart
      bars={bars}
      colors={colors}
      isMini={widget_type === 'mini'}
      legends={legends}
      onBarClick={(columnIndex, subIndex) => {
        if (widget_type !== 'full') return false

        openActionDrawer({
          guid: beatGuid,
          trigger: 'chartBar',
          context: {
            chartBarIndex: columnIndex,
            tabIndex: subIndex,
            block: actionBlocksKey.TRANSCATION_HISTORY_ACTION_BLOCK,
          } satisfies DrilldownPayload,
        })
      }}
    />
  )
}
